import React from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import * as Icons from "lucide-react";
import Seo from "../components/Seo";
import { PageHeader, SectionHeading } from "../components/sections/Shared";
import { Button } from "../components/ui/button";
import { SEO_SERVICES, SEO_CITIES } from "../data/seo";
import { COMPANY } from "../data/mock";

export default function ServicePage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "").replace(/\/$/, "");
  const service = SEO_SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/" replace />;

  const Icon = Icons[service.icon] || Icons.Truck;
  const relatedServices = SEO_SERVICES.filter((s) => s.slug !== slug).slice(0, 3);
  const featuredCities = SEO_CITIES.slice(0, 4);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.h1,
    serviceType: service.h1,
    provider: {
      "@type": "LocalBusiness",
      name: COMPANY.name,
      telephone: "+1-518-954-4701",
      url: "https://518courier.com"
    },
    areaServed: [
      { "@type": "AdministrativeArea", name: "Capital Region, New York" },
      { "@type": "State", name: "New York" },
      { "@type": "State", name: "Massachusetts" },
      { "@type": "State", name: "Connecticut" },
      { "@type": "State", name: "Vermont" }
    ],
    description: service.description,
    mainEntityOfPage: `https://518courier.com/${service.slug}`
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: service.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a }
    }))
  };

  return (
    <>
      <Seo
        title={service.title}
        description={service.description}
        path={`/${service.slug}`}
        jsonLd={[jsonLd, faqLd]}
      />

      <PageHeader
        eyebrow="Our services"
        title={service.h1.split(" ").slice(0, -3).join(" ")}
        accent={service.h1.split(" ").slice(-3).join(" ")}
        description={service.intro}
        image="https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      <section className="py-16 md:py-20 bg-white">
        <div className="container-518 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="w-14 h-14 rounded-2xl bg-[#0f2847] text-white flex items-center justify-center mb-6">
              <Icon className="w-7 h-7" />
            </div>

            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0f2847] mb-3">Who this service is for</h2>
            <p className="text-slate-600 leading-relaxed mb-8">{service.forWhom}</p>

            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0f2847] mb-3">Typical items we handle</h2>
            <ul className="grid sm:grid-cols-2 gap-2.5 mb-8">
              {service.items.map((it, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700">
                  <Icons.CheckCircle2 className="w-5 h-5 text-[#f59321] shrink-0 mt-0.5" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>

            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0f2847] mb-3">Service area</h2>
            <p className="text-slate-600 leading-relaxed mb-10">{service.area}</p>

            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0f2847] mb-4">Frequently asked questions</h2>
            <div className="divide-y divide-slate-200 border-y border-slate-200">
              {service.faqs.map((f, i) => (
                <details key={i} className="group py-4 cursor-pointer">
                  <summary className="flex items-center justify-between gap-4 list-none">
                    <h3 className="font-display font-bold text-[#0f2847]">{f.q}</h3>
                    <Icons.Plus className="w-5 h-5 text-[#f59321] group-open:rotate-45 transition-transform shrink-0" />
                  </summary>
                  <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4 space-y-5 lg:sticky lg:top-28 self-start">
            <div className="bg-[#0f2847] text-white rounded-2xl p-6 stripe-bg">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-2">Request a quote</div>
              <h3 className="font-display text-xl font-extrabold mb-3">Ready to book?</h3>
              <p className="text-sm text-slate-300 mb-5 leading-relaxed">Tell us what you need moved and dispatch will confirm within minutes.</p>
              <Link to="/get-a-quote" className="block">
                <Button className="w-full bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-12 font-semibold btn-lift">
                  Get a Quote
                  <Icons.ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <a href="tel:5189544701" className="mt-3 block">
                <Button variant="outline" className="w-full bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0f2847] rounded-full h-12 font-semibold">
                  <Icons.Phone className="w-4 h-4 mr-2" />
                  Call or text 518-954-4701
                </Button>
              </a>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h4 className="font-display font-bold text-[#0f2847] mb-3 text-sm uppercase tracking-[0.16em]">Related services</h4>
              <ul className="space-y-2">
                {relatedServices.map((s) => (
                  <li key={s.slug}>
                    <Link to={`/${s.slug}`} className="text-sm text-slate-700 hover:text-[#f59321] font-medium">
                      → {s.h1}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h4 className="font-display font-bold text-[#0f2847] mb-3 text-sm uppercase tracking-[0.16em]">Local coverage</h4>
              <ul className="space-y-2">
                {featuredCities.map((c) => (
                  <li key={c.slug}>
                    <Link to={`/${c.slug}`} className="text-sm text-slate-700 hover:text-[#f59321] font-medium">
                      → Courier service in {c.city}, NY
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
